import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Settings, User, Bell, Palette, Shield, Calendar } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { useToast } from "@/components/ui/use-toast";
import { slackService, connectSlackProvider, SlackIntegration } from "@/services/slackService";
import { appleHealthService, AppleHealthIntegration } from "@/services/appleHealthService";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EditProfileForm from "@/components/EditProfileForm";
import EmailNotificationPreferences from "@/components/notifications/EmailNotificationPreferences";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useTheme } from "@/contexts/ThemeContext";

export default function ProfileSettingsWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { user } = useUser();
  const { toast } = useToast();
  const [slackIntegrations, setSlackIntegrations] = useState<SlackIntegration[]>([]);
  const [connectingSlack, setConnectingSlack] = useState(false);
  const [appleHealthIntegrations, setAppleHealthIntegrations] = useState<AppleHealthIntegration[]>([]);
  const [disconnectingAppleHealth, setDisconnectingAppleHealth] = useState(false);

  // Load Slack integrations
  useEffect(() => {
    const loadSlackIntegrations = async () => {
      try {
        const integrations = await slackService.getIntegrations();
        setSlackIntegrations(integrations);
      } catch (error) {
        console.error('Error loading Slack integrations:', error);
      }
    };

    loadSlackIntegrations();
  }, [user?.id]);

  // Load Apple Health integrations
  useEffect(() => {
    const loadAppleHealthIntegrations = async () => {
      try {
        const integrations = await appleHealthService.getIntegrations();
        setAppleHealthIntegrations(integrations);
      } catch (error) {
        console.error('Error loading Apple Health integrations:', error);
      }
    };

    loadAppleHealthIntegrations();
  }, [user?.id]);

  const handleConnectSlack = useCallback(async () => {
    if (!user?.id) {
      toast({
        title: "Authentication required",
        description: "Please sign in to connect Slack.",
        variant: "destructive",
      });
      return;
    }

    setConnectingSlack(true);
    try {
      const success = await connectSlackProvider(user.id);
      setConnectingSlack(false);

      if (success) {
        toast({
          title: "Slack connected",
          description: "Your Slack workspace has been successfully connected!",
        });
        const integrations = await slackService.getIntegrations();
        setSlackIntegrations(integrations);
      } else {
        toast({
          title: "Error connecting Slack",
          description: "Failed to connect Slack workspace. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error connecting Slack:', error);
      setConnectingSlack(false);
      toast({
        title: "Error connecting Slack",
        description: "Failed to connect Slack workspace. Please try again.",
        variant: "destructive",
      });
    }
  }, [user?.id, toast]);

  const handleDisconnectSlack = useCallback(async (integrationId: string) => {
    if (!user?.id) {
      toast({
        title: "Authentication required",
        description: "Please sign in to disconnect Slack.",
        variant: "destructive",
      });
      return;
    }

    setConnectingSlack(true);
    try {
      await slackService.disconnectSlack(integrationId);
      toast({
        title: "Slack disconnected",
        description: "Your Slack workspace has been disconnected.",
      });
      const integrations = await slackService.getIntegrations();
      setSlackIntegrations(integrations);
    } catch (error) {
      console.error('Error disconnecting Slack:', error);
      toast({
        title: "Error disconnecting Slack",
        description: "Failed to disconnect Slack workspace. Please try again.",
        variant: "destructive",
      });
    } finally {
      setConnectingSlack(false);
    }
  }, [user?.id, toast]);

  const handleDisconnectAppleHealth = useCallback(async (integrationId: string) => {
    if (!user?.id) {
      toast({
        title: "Authentication required",
        description: "Please sign in to disconnect Apple Health.",
        variant: "destructive",
      });
      return;
    }

    setDisconnectingAppleHealth(true);
    try {
      await appleHealthService.disconnectAppleHealth(integrationId);
      toast({
        title: "Apple Health disconnected",
        description: "Your Apple Health integration has been disconnected.",
      });
      const integrations = await appleHealthService.getIntegrations();
      setAppleHealthIntegrations(integrations);
    } catch (error) {
      console.error('Error disconnecting Apple Health:', error);
      toast({
        title: "Error disconnecting Apple Health",
        description: "Failed to disconnect Apple Health. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDisconnectingAppleHealth(false);
    }
  }, [user?.id, toast]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-12 w-12 rounded-full bg-background/80 backdrop-blur-sm border-2 hover:bg-background/90 transition-all duration-200 shadow-lg"
          data-testid="profile-settings-trigger"
        >
          <Settings className="h-5 w-5" />
          <span className="sr-only">Profile Settings</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[480px] p-0 ml-4 mb-4" 
        side="right"
        align="end"
        sideOffset={8}
      >
        <div className="max-h-[600px] overflow-y-auto">
          <div className="p-4 border-b bg-muted/50">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Settings
            </h2>
            <p className="text-sm text-muted-foreground">
              Manage your profile and preferences
            </p>
          </div>

          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mx-4 mt-4">
              <TabsTrigger value="profile" className="flex items-center gap-1 text-xs">
                <User className="h-3 w-3" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex items-center gap-1 text-xs">
                <Bell className="h-3 w-3" />
                Notifications
              </TabsTrigger>
              <TabsTrigger value="preferences" className="flex items-center gap-1 text-xs">
                <Palette className="h-3 w-3" />
                Preferences
              </TabsTrigger>
            </TabsList>

            <div className="p-4 space-y-4">
              <TabsContent value="profile" className="mt-0 space-y-4">
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Profile Information
                  </h3>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <EditProfileForm />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="notifications" className="mt-0 space-y-4">
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Bell className="h-4 w-4" />
                    Email Notifications
                  </h3>
                  <div className="bg-muted/30 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                    <EmailNotificationPreferences />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="preferences" className="mt-0 space-y-4">
                <div className="space-y-4">
                  {/* Appearance */}
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Palette className="h-4 w-4" />
                      Appearance
                    </h4>
                    <div className="bg-muted/30 rounded-lg p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="theme" className="text-sm">Theme</Label>
                        <Select 
                          value={theme} 
                          onValueChange={(value: 'light' | 'dark' | 'system') => setTheme(value)}
                        >
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="light">Light</SelectItem>
                            <SelectItem value="dark">Dark</SelectItem>
                            <SelectItem value="system">System</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Working Hours */}
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Working Hours
                    </h4>
                    <div className="bg-muted/30 rounded-lg p-3 space-y-3">
                      <div className="flex items-center gap-2">
                        <Input type="time" defaultValue="09:00" className="w-24 h-8" />
                        <span className="text-sm">to</span>
                        <Input type="time" defaultValue="17:00" className="w-24 h-8" />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Helps schedule reminders and focus time
                      </p>
                    </div>
                  </div>

                  {/* Privacy */}
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Privacy
                    </h4>
                    <div className="bg-muted/30 rounded-lg p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="share-stats" className="text-sm">
                          Share team stats
                        </Label>
                        <Switch id="share-stats" defaultChecked />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="visible-skills" className="text-sm">
                          Visible skills
                        </Label>
                        <Switch id="visible-skills" defaultChecked />
                      </div>
                    </div>
                  </div>

                  {/* Calendar Integration */}
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Integrations
                    </h4>
                    <div className="bg-muted/30 rounded-lg p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Google Calendar</p>
                          <p className="text-xs text-muted-foreground">Connected</p>
                        </div>
                        <Button variant="outline" size="sm" className="h-7 text-xs">
                          Manage
                        </Button>
                      </div>
                      
                      {/* Slack Integration */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Slack</p>
                          <p className="text-xs text-muted-foreground">
                            {slackIntegrations.length > 0 
                              ? slackIntegrations.map(i => i.teamName || 'Workspace').join(', ')
                              : 'Not connected'}
                          </p>
                        </div>
                        {slackIntegrations.length === 0 ? (
                          connectingSlack ? (
                            <Button variant="outline" size="sm" className="h-7 text-xs" disabled>
                              <span className="animate-spin mr-1">⟳</span>
                              Connecting...
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleConnectSlack}>
                              Connect
                            </Button>
                          )
                        ) : (
                          connectingSlack ? (
                            <Button variant="outline" size="sm" className="h-7 text-xs" disabled>
                              <span className="animate-spin mr-1">⟳</span>
                              ...
                            </Button>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 text-xs"
                              onClick={() => handleDisconnectSlack(slackIntegrations[0].id)}
                            >
                              Disconnect
                            </Button>
                          )
                        )}
                      </div>
                      
                      {/* Apple Health Integration */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Apple Health</p>
                          <p className="text-xs text-muted-foreground">
                            {appleHealthIntegrations.length > 0 && appleHealthIntegrations[0].isActive
                              ? 'Connected'
                              : 'Connect on your iOS device'}
                          </p>
                        </div>
                        {appleHealthIntegrations.length > 0 && appleHealthIntegrations[0].isActive ? (
                          disconnectingAppleHealth ? (
                            <Button variant="outline" size="sm" className="h-7 text-xs" disabled>
                              <span className="animate-spin mr-1">⟳</span>
                              ...
                            </Button>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 text-xs"
                              onClick={() => handleDisconnectAppleHealth(appleHealthIntegrations[0].id)}
                            >
                              Disconnect
                            </Button>
                          )
                        ) : (
                          <div className="text-xs text-muted-foreground">
                            iOS only
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </PopoverContent>
    </Popover>
  );
}